import React from 'react';
import { motion } from "motion/react";
import { CalendarIcon, ClockIcon, ShoppingCartIcon } from '@/components/icons';
import { formatDateByTimezone, formatTime, getTimezoneAbbreviation } from '@/lib/utils';
import type { BookingResponse } from '@/features/booking/types/bookingTypes';
import { getTicketCategoryStyle, isPremiumCategory } from './TicketCardStyles';
import { StatusOverlay, isAbandonedStatus } from './StatusOverlay';
import { useStatusTest, useClassTest } from './BookingStatusSwitcher';

interface TicketCardProps {
  booking: BookingResponse;
  onCardClick: () => void;
}

export const TicketCard: React.FC<TicketCardProps> = ({ booking, onCardClick }) => {
  // Handle missing booking
  if (!booking) {
    return null;
  }

  // Get test status and class override from context
  const { getTestStatus } = useStatusTest();
  const { globalTestClass } = useClassTest();
  const testStatus = getTestStatus(booking.id);
  const effectiveStatus = testStatus || booking.status;

  // Handle abandoned status - return null (empty card)
  if (isAbandonedStatus(effectiveStatus)) {
    return null;
  }

  const startTime = booking.start_time;
  // Note: event_booking_category may be null in grouped response
  // We'll show basic info from booking only
  const eventName = booking.event_booking_category?.event_schedule?.event?.name;
  const cartBusinessName = booking.cart?.business?.name;

  // Show event name for event-based bookings, or cart business name for cart-based bookings
  const displayName = eventName || cartBusinessName || 'Booking';

  const bookingIdDisplay = booking.id ? booking.id.split('-')[0] : 'N/A';
  const hasValidId = booking.id && booking.id.trim().length > 0;

  // Check if booking is transferable:
  // redeemed_at is null && add_on_redeemed is false && status == 'Active'
  const isTransferable =
    booking.redeemed_at === null &&
    booking.add_on_redeemed === false &&
    booking.status === 'ACTIVE';

  // Get tier styling from API data (or test override)
  const categoryClass = globalTestClass || booking.booking_category_class;
  const tierStyle = getTicketCategoryStyle(categoryClass);

  const isPremium = isPremiumCategory(categoryClass);

  // Format date and time safely with error handling
  let formattedDate = 'Invalid Date';
  let formattedTime = 'Invalid Time';

  if (startTime) {
    try {
      const date = new Date(startTime);
      if (!isNaN(date.getTime())) {
        const timezone = booking.timezone_id || 'Europe/London';
        formattedDate = `${formatDateByTimezone(startTime, timezone, { format: 'date', includeTimezone: false })} ${getTimezoneAbbreviation(startTime, timezone)}`;
        formattedTime = formatTime(startTime, timezone, false);
      }
    } catch (error) {
      console.error('Error formatting date/time:', error);
    }
  }

  // Format purchase date from cart payment_intent_expires_at
  let formattedPurchaseDate: string | null = null;
  if (booking.cart?.payment_intent_expires_at) {
    try {
      formattedPurchaseDate = formatDateByTimezone(booking.cart.payment_intent_expires_at, booking.timezone_id || 'Europe/London', { format: 'datetime', includeTimezone: false });
    } catch (error) {
      console.error('Error formatting purchase date:', error);
    }
  }

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    // Only call onCardClick if we have a valid booking id
    if (hasValidId) {
      onCardClick();
    }
  };

  return (
    <motion.button
      type="button"
      onClick={handleClick}
      className="w-full text-left relative flex shadow-lg focus:outline-none focus:ring-2 overflow-hidden"
      style={{
        background: tierStyle.backgroundGradient !== 'none' ? tierStyle.backgroundGradient : '#1f2937',
        borderWidth: '1px',
        borderStyle: 'solid',
        borderColor: tierStyle.borderColor,
        clipPath: `polygon(
            0 0,
            20px 0, 25px 4px, 30px 0, 35px 4px, 40px 0, 45px 4px, 50px 0, 55px 4px, 60px 0, 65px 4px, 70px 0, 75px 4px, 80px 0, 85px 4px, 90px 0, 95px 4px, 100px 0,
            calc(100% - 100px) 0, calc(100% - 95px) 4px, calc(100% - 90px) 0, calc(100% - 85px) 4px, calc(100% - 80px) 0, calc(100% - 75px) 4px, calc(100% - 70px) 0, calc(100% - 65px) 4px, calc(100% - 60px) 0, calc(100% - 55px) 4px, calc(100% - 50px) 0, calc(100% - 45px) 4px, calc(100% - 40px) 0, calc(100% - 35px) 4px, calc(100% - 30px) 0, calc(100% - 25px) 4px, calc(100% - 20px) 0, calc(100% - 15px) 4px, calc(100% - 10px) 0,
            100% 0,
            100% calc(50% - 20px),
            calc(100% - 15px) 50%,
            100% calc(50% + 20px),
            100% 100%,
            calc(100% - 10px) 100%, calc(100% - 15px) calc(100% - 4px), calc(100% - 20px) 100%, calc(100% - 25px) calc(100% - 4px), calc(100% - 30px) 100%, calc(100% - 35px) calc(100% - 4px), calc(100% - 40px) 100%, calc(100% - 45px) calc(100% - 4px), calc(100% - 50px) 100%, calc(100% - 55px) calc(100% - 4px), calc(100% - 60px) 100%, calc(100% - 65px) calc(100% - 4px), calc(100% - 70px) 100%, calc(100% - 75px) calc(100% - 4px), calc(100% - 80px) 100%, calc(100% - 85px) calc(100% - 4px), calc(100% - 90px) 100%, calc(100% - 95px) calc(100% - 4px), calc(100% - 100px) 100%,
            100px 100%, 95px calc(100% - 4px), 90px 100%, 85px calc(100% - 4px), 80px 100%, 75px calc(100% - 4px), 70px 100%, 65px calc(100% - 4px), 60px 100%, 55px calc(100% - 4px), 50px 100%, 45px calc(100% - 4px), 40px 100%, 35px calc(100% - 4px), 30px 100%, 25px calc(100% - 4px), 20px 100%, 15px calc(100% - 4px), 10px 100%,
            0 100%,
            0 calc(50% + 20px),
            15px 50%,
            0 calc(50% - 20px),
            0 0
        )`
      }}
      initial={{ opacity: 0, y: 20, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      whileHover={{
        scale: 1.02,
        y: -2,
        boxShadow: `0 20px 40px ${tierStyle.glowColor}`,
        borderColor: tierStyle.hoverBorderColor
      }}
      whileTap={{
        scale: 0.98,
        y: 0
      }}
      transition={{
        type: "spring",
        stiffness: 300,
        damping: 30,
        mass: 0.8
      }}
    >
      {/* Premium tier shimmer effect */}
      {isPremium && (
        <div
          className="absolute inset-0 opacity-10 pointer-events-none"
          style={{
            background: `linear-gradient(45deg, transparent 30%, ${tierStyle.accentColor}20 50%, transparent 70%)`,
            backgroundSize: '200% 100%',
            animation: 'shimmer 3s ease-in-out infinite',
          }}
        />
      )}
      {/* Status Overlay for non-active statuses */}
      <StatusOverlay status={effectiveStatus} variant="card" />
      {/* Mobile-first ticket design */}
      <div className="sm:hidden">
        {/* Mobile Layout - Stacked */}
        <motion.div
          className="p-3"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1, duration: 0.4 }}
        >
          {/* Header */}
          <motion.div
            className="flex justify-between items-start mb-2"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2, duration: 0.4 }}
          >
            <div>
              <div className="flex items-center justify-start gap-1.5 mb-1">
                {tierStyle.badgeSvg && (
                  <div
                    className="w-3.5 h-3.5 flex-shrink-0 flex items-center justify-center"
                    style={{ color: tierStyle.accentColor }}
                    dangerouslySetInnerHTML={{ __html: tierStyle.badgeSvg }}
                  />
                )}
                <p className="text-xs font-bold tracking-wide uppercase leading-none" style={{ color: tierStyle.accentColor }}>
                  {isPremium ? `${categoryClass.toUpperCase()} TICKET` : displayName}
                </p>
                {tierStyle.badgeSvg && (
                  <div
                    className="w-3.5 h-3.5 flex-shrink-0 flex items-center justify-center"
                    style={{ color: tierStyle.accentColor }}
                    dangerouslySetInnerHTML={{ __html: tierStyle.badgeSvg }}
                  />
                )}
              </div>
              <div className="text-xs font-mono" style={{ color: tierStyle.secondaryTextColor }}>
                #{bookingIdDisplay}
              </div>
            </div>
          </motion.div>

          {/* Event name */}
          <motion.h3
            className="text-sm font-serif font-bold text-white mb-3 leading-tight"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.4 }}
          >
            {eventName}
          </motion.h3>

          {/* Date and time in compact row */}
          <motion.div
            className="flex items-center justify-between mb-3"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.4 }}
          >
            <div className="flex items-center gap-2 text-xs">
              <motion.div
                className="w-3 h-3"
                whileHover={{ scale: 1.2, rotate: 5 }}
                transition={{ type: "spring", stiffness: 400, damping: 10 }}
              >
                <CalendarIcon />
              </motion.div>
              <span className="text-gray-300 font-medium">{formattedDate}</span>
            </div>
            <div className="flex items-center gap-2 text-xs">
              <motion.div
                className="w-3 h-3"
                whileHover={{ scale: 1.2, rotate: 5 }}
                transition={{ type: "spring", stiffness: 400, damping: 10 }}
              >
                <ClockIcon />
              </motion.div>
              <span className="text-gray-300 font-medium">{formattedTime}</span>
            </div>
          </motion.div>

          {/* Purchase Date */}
          {formattedPurchaseDate && (
            <motion.div
              className="flex items-center gap-2 text-xs mb-3"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.45, duration: 0.4 }}
            >
              <motion.div
                className="w-3 h-3"
                whileHover={{ scale: 1.2, rotate: 5 }}
                transition={{ type: "spring", stiffness: 400, damping: 10 }}
              >
                <ShoppingCartIcon className="w-3 h-3" />
              </motion.div>
              <span className="text-gray-400 text-xs">Purchase Date: <span className="text-gray-300 font-medium">{formattedPurchaseDate}</span></span>
            </motion.div>
          )}

          {/* Bottom validation */}
          <motion.div
            className="pt-2 border-t border-dashed"
            style={{ borderColor: tierStyle.dashedBorderColor }}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.4 }}
          >
            <div className="text-xs font-mono text-center" style={{ color: tierStyle.secondaryTextColor }}>
              VALID ENTRY
            </div>
          </motion.div>
        </motion.div>
      </div>

      {/* Left side circular cutout */}
      <motion.div
        className="absolute top-1/2 -translate-y-1/2 -left-3 sm:-left-4 lg:-left-5 w-6 h-6 sm:w-8 sm:h-8 lg:w-10 lg:h-10 rounded-full"
        style={{ backgroundColor: tierStyle.cutoutBgColor }}
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ delay: 0.2, type: "spring", stiffness: 400, damping: 25 }}
      ></motion.div>

      {/* Right side circular cutout */}
      <motion.div
        className="absolute top-1/2 -translate-y-1/2 -right-3 sm:-right-4 lg:-right-5 w-6 h-6 sm:w-8 sm:h-8 lg:w-10 lg:h-10 rounded-full"
        style={{ backgroundColor: tierStyle.cutoutBgColor }}
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ delay: 0.2, type: "spring", stiffness: 400, damping: 25 }}
      ></motion.div>

      {/* Main ticket body - Hidden on mobile, shown on larger screens */}
      <div className="hidden sm:flex w-full relative">
        {/* Details Section */}
        <motion.div
          className="p-2 sm:p-4 lg:p-6 flex-1 pl-4 sm:pl-6 lg:pl-8"
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.1, duration: 0.5 }}
        >
          {/* Ticket header */}
          <motion.div
            className="flex justify-between items-start mb-2 sm:mb-3 lg:mb-4"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.4 }}
          >
            <div>
              <div className="flex items-center justify-start gap-1.5 mb-1">
                {tierStyle.badgeSvg && (
                  <div
                    className="w-3.5 h-3.5 flex-shrink-0 flex items-center justify-center"
                    style={{ color: tierStyle.accentColor }}
                    dangerouslySetInnerHTML={{ __html: tierStyle.badgeSvg }}
                  />
                )}
                <p className="text-xs sm:text-xs font-bold tracking-widest uppercase leading-none" style={{ color: tierStyle.accentColor }}>
                  {isPremium ? `${categoryClass.toUpperCase()} TICKET` : displayName}
                </p>
                {tierStyle.badgeSvg && (
                  <div
                    className="w-3.5 h-3.5 flex-shrink-0 flex items-center justify-center"
                    style={{ color: tierStyle.accentColor }}
                    dangerouslySetInnerHTML={{ __html: tierStyle.badgeSvg }}
                  />
                )}
              </div>
              <div className="text-xs font-mono" style={{ color: tierStyle.secondaryTextColor }}>
                #{bookingIdDisplay}
              </div>
            </div>
            <div className="text-xs font-mono" style={{ color: tierStyle.secondaryTextColor }}>
              ADMIT ONE
            </div>
          </motion.div>

          {/* Event name */}
          <motion.h3
            className="text-sm sm:text-base lg:text-xl font-serif font-bold text-white mb-2 sm:mb-3 lg:mb-4 leading-tight line-clamp-2"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.4 }}
          >
            {eventName}
          </motion.h3>

          {/* Date and time */}
          <motion.div
            className="space-y-1 sm:space-y-2"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.4 }}
          >
            <div className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm">
              <motion.div
                className="w-3 h-3 sm:w-4 sm:h-4"
                whileHover={{ scale: 1.2, rotate: 5 }}
                transition={{ type: "spring", stiffness: 400, damping: 10 }}
              >
                <CalendarIcon />
              </motion.div>
              <span className="text-gray-500 uppercase text-xs font-semibold tracking-wide w-8 sm:w-12">Date:</span>
              <span className="text-gray-300 font-medium text-xs sm:text-sm truncate">{formattedDate}</span>
            </div>
            <div className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm">
              <motion.div
                className="w-3 h-3 sm:w-4 sm:h-4"
                whileHover={{ scale: 1.2, rotate: 5 }}
                transition={{ type: "spring", stiffness: 400, damping: 10 }}
              >
                <ClockIcon />
              </motion.div>
              <span className="text-gray-500 uppercase text-xs font-semibold tracking-wide w-8 sm:w-12">Time:</span>
              <span className="text-gray-300 font-medium text-xs sm:text-sm">{formattedTime}</span>
            </div>
            {formattedPurchaseDate && (
              <div className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm">
                <motion.div
                  className="w-3 h-3 sm:w-4 sm:h-4"
                  whileHover={{ scale: 1.2, rotate: 5 }}
                  transition={{ type: "spring", stiffness: 400, damping: 10 }}
                >
                  <ShoppingCartIcon className="w-3 h-3 sm:w-4 sm:h-4" />
                </motion.div>
                <span className="text-gray-500 uppercase text-xs font-semibold tracking-wide w-8 sm:w-12">Purchase:</span>
                <span className="text-gray-300 font-medium text-xs sm:text-sm">{formattedPurchaseDate}</span>
              </div>
            )}
          </motion.div>

          {/* Ticket validation elements */}
          <motion.div
            className="mt-2 sm:mt-3 lg:mt-4 pt-2 sm:pt-3 border-t border-dashed"
            style={{ borderColor: tierStyle.dashedBorderColor }}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.4 }}
          >
            <div className="text-xs font-mono" style={{ color: tierStyle.secondaryTextColor }}>
              VALID FOR ENTRY • {isTransferable ? 'TRANSFERABLE' : 'NON-TRANSFERABLE'}
            </div>
          </motion.div>
        </motion.div>

        {/* Perforated Divider */}
        <motion.div
          className="relative w-px bg-gray-700 mx-2 sm:mx-3 lg:mx-4"
          initial={{ scaleY: 0 }}
          animate={{ scaleY: 1 }}
          transition={{ delay: 0.3, duration: 0.6, ease: "easeOut" }}
        >
          {/* Vertical perforations */}
          <div className="absolute inset-0 flex flex-col justify-between py-1 sm:py-2">
            {Array.from({ length: 6 }).map((_, i) => (
              <motion.div
                key={i}
                className="w-0.5 h-0.5 sm:w-1 sm:h-1 bg-gray-600 rounded-full -translate-x-1/2"
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.4 + i * 0.1, type: "spring", stiffness: 400, damping: 25 }}
              ></motion.div>
            ))}
          </div>
        </motion.div>

        {/* Stub Section */}
        <motion.div
          className="flex flex-col items-center justify-center p-2 sm:p-4 lg:p-6 pr-3 sm:pr-6 lg:pr-8 min-w-[60px] sm:min-w-[80px] lg:min-w-[120px]"
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.4, duration: 0.5 }}
        >
          <motion.div
            className="font-bold tracking-widest -rotate-90 text-xs sm:text-sm uppercase whitespace-nowrap mb-1 sm:mb-2"
            initial={{ opacity: 0, rotate: -90, scale: 0.8 }}
            animate={{ opacity: 1, rotate: -90, scale: 1 }}
            transition={{ delay: 0.5, duration: 0.4 }}
            style={{ writingMode: 'vertical-rl', textOrientation: 'mixed', color: tierStyle.stubTextColor }}
          >
            <span className="hidden lg:inline">Keep This Stub</span>
            <span className="lg:hidden">Stub</span>
          </motion.div>
          <motion.div
            className="transform -rotate-90 text-xs font-mono whitespace-nowrap"
            initial={{ opacity: 0, rotate: -90, scale: 0.8 }}
            animate={{ opacity: 1, rotate: -90, scale: 1 }}
            transition={{ delay: 0.6, duration: 0.4 }}
            style={{ writingMode: 'vertical-rl', textOrientation: 'mixed', color: tierStyle.secondaryTextColor }}
          >
            #{bookingIdDisplay}
          </motion.div>
        </motion.div>

        {/* Vertical dotted line on stub */}
        <motion.div
          className="absolute right-[60px] sm:right-[80px] lg:right-[120px] top-2 sm:top-3 lg:top-4 bottom-2 sm:bottom-3 lg:bottom-4 w-px bg-gray-600"
          initial={{ scaleY: 0 }}
          animate={{ scaleY: 1 }}
          transition={{ delay: 0.5, duration: 0.6, ease: "easeOut" }}
        >
          <div className="absolute inset-0 flex flex-col justify-between py-1 sm:py-2">
            {Array.from({ length: 4 }).map((_, i) => (
              <motion.div
                key={i}
                className="w-1 h-1 sm:w-2 sm:h-2 bg-gray-600 rounded-full -translate-x-1/2"
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.6 + i * 0.1, type: "spring", stiffness: 400, damping: 25 }}
              ></motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </motion.button>
  );
};